import axios from 'axios';
import toast from 'react-hot-toast';

// ✅ Use the current domain - don't hardcode API URL
// This ensures requests go to the correct broker subdomain
const API_BASE_URL = `${window.location.protocol}//${window.location.host}/api`;

console.log('🌐 API Base URL:', API_BASE_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token and broker context
api.interceptors.request.use(
  (config) => {
    // Add auth token if it exists
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // ✅ CRITICAL: Send the current hostname so backend can identify broker
    config.headers['X-Forwarded-Host'] = window.location.hostname;

    // Log request for debugging
    console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`, {
      hostname: window.location.hostname,
      hasToken: !!token,
    });

    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    console.log(`📥 Response from ${response.config.url}:`, response.status);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.error('❌ API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.error || error.message,
    });

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED' && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (refreshToken) {
          console.log('🔄 Attempting token refresh...');
          
          const response = await axios.post(
            `${API_BASE_URL}/broker/auth/refresh`,
            { refreshToken },
            {
              headers: {
                'X-Forwarded-Host': window.location.hostname,
              }
            }
          );

          if (response.data.success && response.data.data.token) {
            const { token } = response.data.data;
            localStorage.setItem('adminToken', token);
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        
        // Clear tokens and redirect to login
        localStorage.removeItem('adminToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors with toast notifications
    if (error.response) {
      const message = error.response.data?.error || 'An error occurred';
      
      // Don't show toast for login errors (handled by form)
      if (!originalRequest.url?.includes('/auth/login')) {
        toast.error(message);
      }
      
      // Handle 404 errors from broker identification
      if (error.response?.status === 404 && error.response?.data?.error?.includes('Broker not found')) {
        console.error('❌ Broker not found for domain:', window.location.hostname);
      }
    } else if (error.request) {
      toast.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

// ==================== AUTH API ====================

export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/broker/auth/login', { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/broker/auth/logout');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('refreshToken');
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/broker/auth/me');
    return response.data;
  },

  refreshToken: async (refreshToken) => {
    const response = await api.post('/broker/auth/refresh', { refreshToken });
    return response.data;
  },
};

// ==================== PUBLIC API ====================

export const publicAPI = {
  getBroker: async () => {
    const response = await api.get('/public/broker');
    return response.data;
  },
  
  getMarketStatus: async () => {
    const response = await api.get('/public/market-status');
    return response.data;
  },
};

// ==================== DASHBOARD API ====================

export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/broker/dashboard/stats');
    return response.data;
  },
};

// ==================== INSTRUMENTS API ====================

export const instrumentsAPI = {
  getFeed: async (params = {}) => {
    const response = await api.get('/broker/instruments/feed', { params });
    return response.data;
  },

  getEnabled: async (params = {}) => {
    const response = await api.get('/broker/instruments/enabled', { params });
    return response.data;
  },

  enable: async (symbol, settings = {}) => {
    const response = await api.post('/broker/instruments/enable', { symbol, settings });
    return response.data;
  },

  disable: async (symbol) => {
    const response = await api.post('/broker/instruments/disable', { symbol });
    return response.data;
  },

  bulkEnable: async (symbols, defaultSettings = {}) => {
    const response = await api.post('/broker/instruments/bulk-enable', { symbols, defaultSettings });
    return response.data;
  },

  // ✅ FIXED: Use query params for symbols with slashes
  getSettings: async (symbol) => {
    const response = await api.get('/broker/instruments/settings', { 
      params: { symbol } 
    });
    return response.data;
  },

  updateSettings: async (symbol, settings) => {
    const response = await api.put('/broker/instruments/settings', settings, { 
      params: { symbol } 
    });
    return response.data;
  },
};

// ==================== CLIENTS API ====================

export const clientsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/broker/clients', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/broker/clients/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/broker/clients/${id}`, data);
    return response.data;
  },
};

// ==================== ACCOUNTS API ====================

export const accountsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/broker/accounts', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/broker/accounts/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/broker/accounts/${id}`, data);
    return response.data;
  },

  credit: async (id, amount, description) => {
    const response = await api.post(`/broker/accounts/${id}/credit`, { amount, description });
    return response.data;
  },

  debit: async (id, amount, description) => {
    const response = await api.post(`/broker/accounts/${id}/debit`, { amount, description });
    return response.data;
  },
};

// ==================== TRADING API ====================

export const tradingAPI = {
  getPositions: async (params = {}) => {
    const response = await api.get('/broker/positions', { params });
    return response.data;
  },

  getOrders: async (params = {}) => {
    const response = await api.get('/broker/orders', { params });
    return response.data;
  },

  getHistory: async (params = {}) => {
    const response = await api.get('/broker/trades', { params });
    return response.data;
  },
  
  closePosition: async (positionId, accountId) => {
    const response = await api.post(`/broker/positions/${positionId}/close`, { accountId });
    return response.data;
  },
  
  modifyPosition: async (positionId, accountId, updates) => {
    const response = await api.put(`/broker/positions/${positionId}`, { accountId, ...updates });
    return response.data;
  },
  
  cancelOrder: async (orderId, accountId) => {
    const response = await api.delete(`/broker/orders/${orderId}`, { data: { accountId } });
    return response.data;
  },
  
  modifyOrder: async (orderId, accountId, updates) => {
    const response = await api.put(`/broker/orders/${orderId}`, { accountId, ...updates });
    return response.data;
  },
};

// ==================== REPORTS API ====================

// ==================== REPORTS API ====================

export const reportsAPI = {
  getDashboard: async (params = {}) => {
    const response = await api.get('/broker/reports/dashboard', { params });
    return response.data;
  },

  getPaymentBreakdown: async (params = {}) => {
    const response = await api.get('/broker/reports/payment-breakdown', { params });
    return response.data;
  },

  getTimeline: async (days = 30) => {
    const response = await api.get('/broker/reports/timeline', { params: { days } });
    return response.data;
  },

  getRiskyAccounts: async (limit = 20) => {
    const response = await api.get('/broker/reports/risky-accounts', { params: { limit } });
    return response.data;
  },

  getPendingRequests: async () => {
    const response = await api.get('/broker/reports/pending-requests');
    return response.data;
  },

  getDaily: async (date) => {
    const response = await api.get('/broker/reports/daily', { params: { date } });
    return response.data;
  },
};

// ==================== SETTINGS API ====================

export const settingsAPI = {
  getGroups: async () => {
    const response = await api.get('/broker/groups');
    return response.data;
  },
  
  getGroup: async (id) => {
    const response = await api.get(`/broker/groups/${id}`);
    return response.data;
  },

  createGroup: async (data) => {
    const response = await api.post('/broker/groups', data);
    return response.data;
  },

  updateGroup: async (id, data) => {
    const response = await api.put(`/broker/groups/${id}`, data);
    return response.data;
  },
  
  deleteGroup: async (id) => {
    const response = await api.delete(`/broker/groups/${id}`);
    return response.data;
  },

  getBroker: async () => {
    const response = await api.get('/broker/settings');
    return response.data;
  },

  updateBroker: async (data) => {
    const response = await api.put('/broker/settings', data);
    return response.data;
  },
};

// ==================== USER OVERRIDES API ====================

export const userOverridesAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/broker/user-overrides', { params });
    return response.data;
  },
  
  getByUserId: async (userId) => {
    const response = await api.get(`/broker/user-overrides/user/${userId}`);
    return response.data;
  },
  
  create: async (userId, data) => {
    const response = await api.post(`/broker/user-overrides/user/${userId}`, data);
    return response.data;
  },
  
  update: async (userId, data) => {
    const response = await api.put(`/broker/user-overrides/user/${userId}`, data);
    return response.data;
  },
  
  delete: async (userId) => {
    const response = await api.delete(`/broker/user-overrides/user/${userId}`);
    return response.data;
  },
  
  upsert: async (userId, data) => {
    const response = await api.post(`/broker/user-overrides/user/${userId}/upsert`, data);
    return response.data;
  },
};

// ==================== PLUGINS API ====================

export const pluginsAPI = {
  /**
   * Get all plugins (purchased + available)
   * GET /api/broker/plugins
   */
  getAll: async () => {
    const response = await api.get('/broker/plugins');
    return response.data.data; // { purchased: [...], available: [...] }
  },

  /**
   * Get only enabled plugins
   * GET /api/broker/plugins/enabled
   */
  getEnabled: async () => {
    const response = await api.get('/broker/plugins/enabled');
    return response.data.data;
  },

  /**
   * Get sidebar plugins
   * GET /api/broker/plugins/sidebar
   */
  getSidebar: async () => {
    const response = await api.get('/broker/plugins/sidebar');
    return response.data.data;
  },

  /**
   * Get single plugin details
   * GET /api/broker/plugins/:pluginKey
   */
  getByKey: async (pluginKey) => {
    const response = await api.get(`/broker/plugins/${pluginKey}`);
    return response.data.data;
  },

  /**
   * Enable a plugin
   * POST /api/broker/plugins/:pluginKey/enable
   */
  enable: async (pluginKey, expiresAt = null) => {
    const response = await api.post(`/broker/plugins/${pluginKey}/enable`, { expiresAt });
    return response.data.data;
  },

  /**
   * Disable a plugin
   * POST /api/broker/plugins/:pluginKey/disable
   */
  disable: async (pluginKey) => {
    const response = await api.post(`/broker/plugins/${pluginKey}/disable`);
    return response.data.data;
  },

  /**
   * Check if plugin is enabled
   * GET /api/broker/plugins/:pluginKey/check
   */
  check: async (pluginKey) => {
    const response = await api.get(`/broker/plugins/${pluginKey}/check`);
    return response.data.data; // { enabled: true/false }
  },



  /**
   * Get available crypto payment options (networks)
   * GET /api/broker/plugins/payment-options
   */
  getPaymentOptions: async () => {
    const response = await api.get('/broker/plugins/payment-options');
    return response.data.data;
  },

  /**
   * Purchase a plugin with crypto
   * POST /api/broker/plugins/:pluginKey/purchase
   */
  purchase: async (pluginKey, paymentData) => {
    const response = await api.post(`/broker/plugins/${pluginKey}/purchase`, paymentData);
    return response.data.data;
  },

  /**
   * Poll payment status
   * GET /api/broker/plugins/payments/:paymentId/status
   */
  getPaymentStatus: async (paymentId) => {
    const response = await api.get(`/broker/plugins/payments/${paymentId}/status`);
    return response.data.data;
  },

  /**
   * Get payment history
   * GET /api/broker/plugins/payments
   */
  getPaymentHistory: async (params = {}) => {
    const response = await api.get('/broker/plugins/payments', { params });
    return response.data.data;
  },

  /**
   * Get active subscriptions
   * GET /api/broker/plugins/subscriptions
   */
  getSubscriptions: async () => {
    const response = await api.get('/broker/plugins/subscriptions');
    return response.data.data;
  },
  
};

// ==================== PAYMENTS API ====================

export const paymentsAPI = {
  // ── Deposit queue ──────────────────────────────────────
  getDeposits: async (params = {}) => {
    const response = await api.get('/broker/payments/deposits', { params });
    return response.data.data;
  },

  approveDeposit: async (id, data = {}) => {
    const response = await api.put(`/broker/payments/deposits/${id}/approve`, data);
    return response.data.data;
  },

  rejectDeposit: async (id, data = {}) => {
    const response = await api.put(`/broker/payments/deposits/${id}/reject`, data);
    return response.data.data;
  },

  // ── Withdrawal queue ───────────────────────────────────
  getWithdrawals: async (params = {}) => {
    const response = await api.get('/broker/payments/withdrawals', { params });
    return response.data.data;
  },

  approveWithdrawal: async (id, data = {}) => {
    const response = await api.put(`/broker/payments/withdrawals/${id}/approve`, data);
    return response.data.data;
  },

  rejectWithdrawal: async (id, data = {}) => {
    const response = await api.put(`/broker/payments/withdrawals/${id}/reject`, data);
    return response.data.data;
  },

  // ── Audit log ──────────────────────────────────────────
  getAuditLog: async (params = {}) => {
    const response = await api.get('/broker/payments/audit-log', { params });
    return response.data.data;
  },

  // ── Bank accounts CRUD ─────────────────────────────────
  getBankAccounts: async () => {
    const response = await api.get('/broker/payments/bank-accounts');
    return response.data.data;
  },

  createBankAccount: async (data) => {
    const response = await api.post('/broker/payments/bank-accounts', data);
    return response.data.data;
  },

  updateBankAccount: async (id, data) => {
    const response = await api.put(`/broker/payments/bank-accounts/${id}`, data);
    return response.data.data;
  },

  deleteBankAccount: async (id) => {
    const response = await api.delete(`/broker/payments/bank-accounts/${id}`);
    return response.data.data;
  },

  // ── Per-user bank assignment ───────────────────────────
  assignBankToUser: async (bankId, data) => {
    const response = await api.post(`/broker/payments/bank-accounts/${bankId}/assign-user`, data);
    return response.data.data;
  },

  removeUserAssignment: async (bankId, userId) => {
    const response = await api.delete(`/broker/payments/bank-accounts/${bankId}/users/${userId}`);
    return response.data.data;
  },

  // ── Per-country bank mapping ───────────────────────────
  mapBankToCountry: async (bankId, data) => {
    const response = await api.post(`/broker/payments/bank-accounts/${bankId}/map-country`, data);
    return response.data.data;
  },

  removeCountryMapping: async (bankId, countryCode) => {
    const response = await api.delete(`/broker/payments/bank-accounts/${bankId}/countries/${countryCode}`);
    return response.data.data;
  },

  // ── Crypto wallets CRUD ────────────────────────────────
  getCryptoWallets: async () => {
    const response = await api.get('/broker/payments/crypto-wallets');
    return response.data.data;
  },

  createCryptoWallet: async (data) => {
    const response = await api.post('/broker/payments/crypto-wallets', data);
    return response.data.data;
  },

  updateCryptoWallet: async (id, data) => {
    const response = await api.put(`/broker/payments/crypto-wallets/${id}`, data);
    return response.data.data;
  },

  deleteCryptoWallet: async (id) => {
    const response = await api.delete(`/broker/payments/crypto-wallets/${id}`);
    return response.data.data;
  },

  // ── Payment links CRUD ─────────────────────────────────
  getPaymentLinks: async () => {
    const response = await api.get('/broker/payments/payment-links');
    return response.data.data;
  },

  createPaymentLink: async (data) => {
    const response = await api.post('/broker/payments/payment-links', data);
    return response.data.data;
  },

  updatePaymentLink: async (id, data) => {
    const response = await api.put(`/broker/payments/payment-links/${id}`, data);
    return response.data.data;
  },

  deletePaymentLink: async (id) => {
    const response = await api.delete(`/broker/payments/payment-links/${id}`);
    return response.data.data;
  },
};

// ==================== KYC API ====================

export const kycAPI = {
  /**
   * Get broker's KYC configuration
   * GET /api/broker/kyc/config
   */
  getConfig: () => api.get('/broker/kyc/config'),
  
  /**
   * Update broker's KYC configuration
   * PUT /api/broker/kyc/config
   */
  updateConfig: (data) => api.put('/broker/kyc/config', data),
  
  /**
   * Get pending KYC applications
   * GET /api/broker/kyc/pending?level=basic&limit=50
   */
  getPending: (params = {}) => api.get('/broker/kyc/pending', { params }),
  
  /**
   * Get user's complete KYC profile
   * GET /api/broker/kyc/users/:userId
   */
  getUserProfile: (userId) => api.get(`/broker/kyc/users/${userId}`),
  
  /**
   * Approve or reject KYC application
   * POST /api/broker/kyc/review/:userId/:level
   * Body: { action: 'approve' | 'reject', reason?: string }
   */
  reviewApplication: (userId, level, data) => 
    api.post(`/broker/kyc/review/${userId}/${level}`, data),
  
  /**
   * Get KYC statistics
   * GET /api/broker/kyc/statistics
   */
  getStatistics: () => api.get('/broker/kyc/statistics'),
  
  /**
   * Get audit log
   * GET /api/broker/kyc/audit-log?action=approved&level=basic&limit=100
   */
  getAuditLog: (params = {}) => api.get('/broker/kyc/audit-log', { params }),

  getAllApplications:  (params = {})  => api.get('/broker/kyc/applications', { params }),
};

// ──────────────────────────────────────────────────────────────────────────────
// ADD TO src/services/api.js
// Add the subscriptionAPI export alongside your existing exports
// ──────────────────────────────────────────────────────────────────────────────

export const subscriptionAPI = {
  /**
   * Get broker's current subscription status
   * GET /api/broker/subscription
   */
  getSubscription: async () => {
    const response = await api.get('/broker/subscription');
    return response.data;
  },

  /**
   * Get all available plans
   * GET /api/broker/subscription/plans
   */
  getPlans: async () => {
    const response = await api.get('/broker/subscription/plans');
    return response.data;
  },

  /**
   * Initiate a subscription payment
   * POST /api/broker/subscription/pay
   */
  initiatePayment: async ({ plan_id, billing_cycle, currency, network }) => {
    const response = await api.post('/broker/subscription/pay', {
      plan_id, billing_cycle, currency, network,
    });
    return response.data;
  },

  /**
   * Poll payment status during checkout
   * GET /api/broker/subscription/payment/:paymentId/status
   */
  getPaymentStatus: async (paymentId) => {
    const response = await api.get(`/broker/subscription/payment/${paymentId}/status`);
    return response.data;
  },
};

export const PAYMENT_TYPES = {
  PLUGIN_PURCHASE: 'plugin_purchase',
  DEPOSIT: 'deposit',
  WITHDRAWAL: 'withdrawal',
  SUBSCRIPTION: 'subscription',
};


// ==================== CREDITS API ====================
export const creditsAPI = {
  // ── Bonus rules ────────────────────────────────────────
  getRules: async (params = {}) => {
    const response = await api.get('/broker/credits/rules', { params });
    return response.data;
  },

  createRule: async (data) => {
    const response = await api.post('/broker/credits/rules', data);
    return response.data;
  },

  updateRule: async (id, data) => {
    const response = await api.put(`/broker/credits/rules/${id}`, data);
    return response.data;
  },

  deleteRule: async (id) => {
    const response = await api.delete(`/broker/credits/rules/${id}`);
    return response.data;
  },

  // ── Manual award ───────────────────────────────────────
  awardManual: async (data) => {
    const response = await api.post('/broker/credits/award', data);
    return response.data;
  },

  // ── History ────────────────────────────────────────────
  getAllCredits: async (params = {}) => {
    const response = await api.get('/broker/credits', { params });
    return response.data;
  },

  getAccountCredits: async (accountId, params = {}) => {
    const response = await api.get(`/broker/credits/accounts/${accountId}`, { params });
    return response.data;
  },

  // ── Revoke ─────────────────────────────────────────────
  revokeCredit: async (id, reason) => {
    const response = await api.put(`/broker/credits/${id}/revoke`, { reason });
    return response.data;
  },
};


export const emailTemplatesAPI = {

  // ── Templates ───────────────────────────────────────────────
  getAll: async () => {
    const response = await api.get('/broker/email-templates');
    return response.data.data;
  },

  getOne: async (templateType) => {
    const response = await api.get(`/broker/email-templates/${templateType}`);
    return response.data.data;
  },

  save: async (templateType, data) => {
    const response = await api.put(`/broker/email-templates/${templateType}`, data);
    return response.data;
  },

  reset: async (templateType) => {
    const response = await api.delete(`/broker/email-templates/${templateType}`);
    return response.data;
  },

  // ── Branding — read-only ────────────────────────────────────
  // Sourced from brokers table. Update via broker settings page.
  getBranding: async () => {
    const response = await api.get('/broker/email-templates/branding');
    return response.data.data;
  },
};

export const pagesAPI = {
  /** Get all pages — GET /api/broker/pages */
  getAll: async () => {
    const response = await api.get('/broker/pages');
    return response.data;
  },

  /** Get single page — GET /api/broker/pages/:pageKey */
  getOne: async (pageKey) => {
    const response = await api.get(`/broker/pages/${pageKey}`);
    return response.data;
  },

  /** Update page — PUT /api/broker/pages/:pageKey */
  update: async (pageKey, data) => {
    const response = await api.put(`/broker/pages/${pageKey}`, data);
    return response.data;
  },

  /** Publish page — POST /api/broker/pages/:pageKey/publish */
  publish: async (pageKey) => {
    const response = await api.post(`/broker/pages/${pageKey}/publish`);
    return response.data;
  },

  /** Unpublish page — POST /api/broker/pages/:pageKey/unpublish */
  unpublish: async (pageKey) => {
    const response = await api.post(`/broker/pages/${pageKey}/unpublish`);
    return response.data;
  },
};

export const analyticsAPI = {
  // Dashboard
  getDashboard: () => api.get('/broker/analytics/dashboard'),
  
  // Risk Intelligence
  getRiskHeatmap: () => api.get('/broker/analytics/risk-heatmap'),
  getRiskPredictions: (timeframe = '24h') => 
    api.get(`/broker/analytics/risk-predictions?timeframe=${timeframe}`),
  
  // CLV
  getCLV: () => api.get('/broker/analytics/clv'),
  getCLVSegments: () => api.get('/broker/analytics/clv/segments'),
  
  // Symbol Intelligence
  getSymbolIntelligence: (timeRange = 30) => 
    api.get(`/broker/analytics/symbols?timeRange=${timeRange}`),
  getSymbolDetails: (symbol, timeRange = 30) => 
    api.get(`/broker/analytics/symbols/${encodeURIComponent(symbol)}?timeRange=${timeRange}`),
  
  // Patterns
  getTradingPatterns: () => api.get('/broker/analytics/patterns'),
  
  // Revenue
  getRevenueAnalytics: (timeRange = 30) => 
    api.get(`/broker/analytics/revenue?timeRange=${timeRange}`),
  
  // Cohorts
  getCohortAnalysis: (groupBy = 'month') => 
    api.get(`/broker/analytics/cohorts?groupBy=${groupBy}`),
  
  // Alerts
  getAlerts: () => api.get('/broker/analytics/alerts'),
  createAlert: (data) => api.post('/broker/analytics/alerts', data),
  updateAlert: (id, data) => api.put(`/broker/analytics/alerts/${id}`, data),
  deleteAlert: (id) => api.delete(`/broker/analytics/alerts/${id}`),
  getAlertHistory: (limit = 50, offset = 0) => 
    api.get(`/broker/analytics/alerts/history?limit=${limit}&offset=${offset}`),
  acknowledgeAlert: (id) => api.post(`/broker/analytics/alerts/${id}/acknowledge`),
  
  // Export
  exportAnalytics: (reportType, format, dateRange) => 
    api.post('/broker/analytics/export', { reportType, format, dateRange }, {
      responseType: 'blob'
    }),
  
  // Insights
  getInsights: () => api.get('/broker/analytics/insights'),
  getRecommendations: () => api.get('/broker/analytics/recommendations'),
};


export const notificationAPI = {
  // Get all notification settings (email, push, sms, routing matrix)
  getSettings: () =>
    api.get('/broker/notification-settings'),

  // Save email/push/sms settings
  saveSettings: (data) =>
    api.put('/broker/notification-settings', data),

  // Test a channel — channel is 'email' | 'push' | 'sms'
  test: (channel) =>
    api.post(`/broker/notification-settings/test/${channel}`),

  // Upload Firebase service account JSON (server-side credentials)
  uploadFCM: (file) => {
    const form = new FormData();
    form.append('credentials', file);
    return api.post('/broker/notification-settings/fcm-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Save Firebase web app config (public keys for client SDK)
  saveWebConfig: (config) =>
    api.post('/broker/notification-settings/fcm-web-config', config),
};

// ==================== RISK GUARD API ====================
export const riskGuardAPI = {
  /** GET /api/broker/plugins/risk-guard/config */
  getConfig: async () => {
    const response = await api.get('/broker/plugins/risk-guard/config');
    return response.data;
  },

  /** POST /api/broker/plugins/risk-guard/config */
  saveConfig: async (data) => {
    const response = await api.post('/broker/plugins/risk-guard/config', data);
    return response.data;
  },

  /** GET /api/broker/plugins/risk-guard/stats */
  getStats: async () => {
    const response = await api.get('/broker/plugins/risk-guard/stats');
    return response.data;
  },

  /** GET /api/broker/plugins/risk-guard/events */
  getEvents: async (params = {}) => {
    const response = await api.get('/broker/plugins/risk-guard/events', { params });
    return response.data;
  },
};

// ==================== POSITION MONITOR API ====================
export const positionMonitorAPI = {
  /** GET /api/broker/plugins/position-monitor/config */
  getConfig: async () => {
    const response = await api.get('/broker/plugins/position-monitor/config');
    return response.data;
  },

  /** POST /api/broker/plugins/position-monitor/config */
  saveConfig: async (data) => {
    const response = await api.post('/broker/plugins/position-monitor/config', data);
    return response.data;
  },

  /** GET /api/broker/plugins/position-monitor/stats */
  getStats: async () => {
    const response = await api.get('/broker/plugins/position-monitor/stats');
    return response.data;
  },

  /** GET /api/broker/plugins/position-monitor/events */
  getEvents: async (params = {}) => {
    const response = await api.get('/broker/plugins/position-monitor/events', { params });
    return response.data;
  },
};


export const mediaAPI = {
  upload: (formData) => api.post('/broker/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  list: (params) => api.get('/broker/media', { params }),
  delete: (id) => api.delete(`/broker/media/${id}`),
};
export default api;